/**
 * Wizard step `claims` (§16.1 step 7, §5.3 Claims, §11): claim window, variant hold and auto-extensions,
 * required fields by fulfillment type (legal name + government ID by default), verification adapter (D08),
 * retention class, unclaimed-prize policy, appeal window and support SLA. Binds the `claims` slice.
 */
import { ClaimField, FulfillmentType, type ClaimField as ClaimFieldT } from '@windrop/contracts';
import { useFormContext, useWatch } from 'react-hook-form';
import { FormErrorSummary, RhfSelect, RhfText, fieldId } from '../../../components/FormField';
import { useWizard } from './WizardFrame';
import { stepForPath, type DraftConfigInput } from './steps';

const intReq = { parse: (raw: string) => Number(raw), format: (v: unknown) => String(v ?? 0) };
const FIELD_LABEL: Record<ClaimFieldT, string> = {
  LEGAL_NAME: 'Legal name',
  GOVERNMENT_ID: 'Government ID (type + number)',
  ID_DOCUMENT_IMAGE: 'ID document image',
  WHATSAPP_NUMBER: 'WhatsApp number',
  SOCIAL_HANDLE: 'Social handle',
  EMAIL: 'Email',
  DELIVERY_ADDRESS: 'Delivery address',
  PICKUP_CHOICE: 'Pickup choice',
  PAYOUT_DESTINATION: 'Payout destination',
  TAX_FIELDS: 'Tax fields',
  DELIVERY_DESTINATION: 'Delivery destination (digital)',
  APPOINTMENT: 'Appointment',
  ADDITIONAL_DOCUMENTS: 'Additional documents',
};

export function ClaimsStep() {
  const { control } = useFormContext<DraftConfigInput>();
  const { issues, goToStep } = useWizard();
  const mine = issues.filter((i) => i.step === 'claims');
  const claims = useWatch({ control, name: 'claims' });
  const tz = useWatch({ control, name: 'identity.timezone' }) as string | undefined;

  return (
    <div className="wd-stack" data-testid="claims-step">
      <FormErrorSummary
        issues={mine}
        title={`${mine.length} issue${mine.length === 1 ? '' : 's'} in claims`}
        onNavigate={(p) => goToStep(stepForPath(p))}
      />

      <h2>Claim window and holds (§2.4, §6.2)</h2>
      <div className="wdc-form-grid">
        <RhfText
          control={control}
          name="claims.claimWindowDays"
          label="Claim window (calendar days)"
          type="number"
          required
          hint={`Fourteen by default, displayed in ${tz ?? 'the lottery timezone'}; zero is a publish blocker.`}
          {...intReq}
        />
        <RhfText
          control={control}
          name="claims.variantHoldMinutes"
          label="Variant hold (minutes)"
          type="number"
          required
          hint="Fifteen minutes with a visible timer."
          {...intReq}
        />
        <RhfText
          control={control}
          name="claims.holdAutoExtensions"
          label="Automatic hold extensions"
          type="number"
          required
          hint="One while verification waits on the platform."
          {...intReq}
        />
        <RhfSelect
          control={control}
          name="claims.unclaimedPrizePolicy"
          label="Unclaimed-prize policy"
          required
          options={[
            { value: 'REDRAW', label: 'One audited redraw (default)' },
            { value: 'ROLLOVER', label: 'Roll over' },
            { value: 'FORFEIT', label: 'Forfeit' },
          ]}
        />
        <RhfText
          control={control}
          name="claims.appealWindowDays"
          label="Appeal window after rejection (days)"
          type="number"
          required
          {...intReq}
        />
      </div>

      <h2>Required fields by fulfillment type (§11.1, D08)</h2>
      <p className="wd-muted">
        Legal name and government ID are collected by default; raw ID images are deleted 90 days after claim
        closure unless a legal hold applies (§2.4).
      </p>
      <div className="wdc-form-grid">
        {FulfillmentType.options.map((type) => (
          <RequiredFields key={type} type={type} />
        ))}
      </div>

      <h2>Verification and retention (D08, D16, §17.3)</h2>
      <div className="wdc-form-grid">
        <RhfSelect
          control={control}
          name="claims.verificationAdapter"
          label="Verification adapter"
          required
          options={[
            { value: 'mock', label: 'Mock (until the client names its solution)' },
            { value: 'manual', label: 'Manual review (signed export / import)' },
          ]}
          hint="The platform collects, hands off and records; it never decides identity itself."
        />
        <RhfSelect
          control={control}
          name="claims.retentionClass"
          label="Retention class"
          required
          options={[
            { value: 'STANDARD_90D', label: 'Standard — 90 days after closure' },
            { value: 'MINIMAL_30D', label: 'Minimal — 30 days' },
            { value: 'EXTENDED_LEGAL_HOLD', label: 'Extended — legal hold' },
          ]}
        />
      </div>

      <h2>Support service levels (§11.3, §11.4)</h2>
      <div className="wdc-form-grid">
        <RhfText
          control={control}
          name="claims.supportSla.firstResponseMinutesLive"
          label="First response during live events (minutes)"
          type="number"
          required
          {...intReq}
        />
        <RhfText
          control={control}
          name="claims.supportSla.firstResponseBusinessHours"
          label="First response otherwise (business hours)"
          type="number"
          required
          {...intReq}
        />
        <RhfText
          control={control}
          name="claims.supportSla.reviewBusinessDays"
          label="Claim review (business days)"
          type="number"
          required
          {...intReq}
        />
      </div>
      <p className="wd-muted" id={fieldId('claims')}>
        Summary: {claims?.claimWindowDays ?? 14}-day claim window, {claims?.variantHoldMinutes ?? 15}-minute
        hold with {claims?.holdAutoExtensions ?? 1} extension(s), unclaimed →{' '}
        {String(claims?.unclaimedPrizePolicy ?? 'REDRAW').toLowerCase()}.
      </p>
    </div>
  );
}

function RequiredFields({ type }: { type: (typeof FulfillmentType.options)[number] }) {
  const { control, setValue } = useFormContext<DraftConfigInput>();
  const name = `claims.requiredFields.${type}` as const;
  const list = (useWatch({ control, name }) as ClaimFieldT[] | undefined) ?? [];
  return (
    <fieldset className="wdc-fieldset" id={fieldId(name)} data-testid={`required-${type}`}>
      <legend>{type.toLowerCase().replace(/_/g, ' ')}</legend>
      <div className="wdc-check-list" data-cols="2">
        {ClaimField.options.map((f) => (
          <label key={f}>
            <input
              type="checkbox"
              checked={list.includes(f)}
              onChange={(e) =>
                setValue(name, e.target.checked ? [...list, f] : list.filter((x) => x !== f), {
                  shouldDirty: true,
                })
              }
            />
            <span>{FIELD_LABEL[f]}</span>
          </label>
        ))}
      </div>
    </fieldset>
  );
}
